import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { Check, Calendar, Clock, Music, User, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleConfirmPage() {
  const { scheduleId, personId } = useParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    loadScheduleInfo();
  }, [scheduleId, personId]);

  const loadScheduleInfo = async () => {
    try {
      const response = await axios.get(`${API}/schedules/confirm-page/${scheduleId}/${personId}`);
      setScheduleInfo(response.data);
      setConfirmed(response.data.confirmed);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar informações da escala");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.get(`${API}/schedules/confirm/${scheduleId}/${personId}`);
      setConfirmed(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao confirmar presença");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">!</span>
            </div>
            <h2 className="text-xl font-bold text-red-600 mb-2">Erro</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Presença Confirmada!</h2>
            <p className="text-muted-foreground mb-4">
              Obrigado por confirmar sua presença, {scheduleInfo?.person_name}!
            </p>
            <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
              <p className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="font-medium">{scheduleInfo?.event_name}</span>
              </p>
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{scheduleInfo?.event_date} às {scheduleInfo?.event_time}</span>
              </p>
              <p className="flex items-center gap-2">
                <Music className="h-4 w-4 text-muted-foreground" />
                <span>{scheduleInfo?.ministry_name}</span>
              </p>
              <p className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{scheduleInfo?.function_name}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Confirmar Presença</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-lg font-medium">{scheduleInfo?.person_name}</p>
            <Badge variant="outline" className="mt-2">{scheduleInfo?.function_name}</Badge>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">{scheduleInfo?.event_name}</p>
                <p className="text-sm text-muted-foreground">{scheduleInfo?.event_date} às {scheduleInfo?.event_time}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Music className="h-5 w-5 text-muted-foreground" />
              <p>{scheduleInfo?.ministry_name}</p>
            </div>
          </div>

          <Button 
            className="w-full" 
            size="lg"
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="confirm-schedule-btn"
          >
            {confirming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Confirmando...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirmar Presença
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
